import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useToast } from "../../../../../app/hooks/useToast";
import { InputBox } from "../../styles";
import { Input } from "../../../../../components/common/Input";
import { motion } from "framer-motion";
import { Button } from "../../../../../components/common/Button";
import { nextVariants } from "../../../../Wallets/utils";
import CustomSelect from "../../../../../components/common/CustomSelect";
import Loader from "../../../../../components/common/Loader";
import { usePopulateFormFields } from "../../../../../app/hooks/usePopulateFormFields";
import { useBankList } from "../../../../../app/hooks/useBankList";
import { Form } from "../../../../Wallets/components/SendFunds/components/StepAmount/style";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { createUpdateBankAccount } from "../../../../Settings/settingsAPI";
import { getBankAccountDetail } from "../../api";
import { ghsPayoutMethods, GHS_PAYMENT_DESCRIPTION } from "../../../../Wallets/const";
import { setSettlementDetails } from "../../SettlementPayoutSlice";
import { settlementMethodCountryMap, settlementMethodCurrencyMap } from "../../../../Settlements/utils";
import ErrorMessage from "../../../../../components/ErrorMessage";
import {
  capitalizeCurrencyCode,
  getAccountNumberErrorMessage,
  getAccountNumberMaxLength,
  getAccountNumberPattern,
  GHS_PHONE_NUMBER_ERROR_MESSAGE,
  NGN_ACCOUNT_NUMBER_LENGTH,
  sanitizeAccountNumberInput,
} from "../../../../../utils/helpers";

type LocalBeneficiaryState ={
  loading: boolean;
  activeBank: string | null;
  error: string;
}

interface FormInputs {
  bankName?: string;
  accountName?: string;
  accountNumber?: string;
  label?: string;
  value?: string;
  account_name?: string;
  account_number?: string;

  payoutMethod?: string;
  paymentMethod?: string;
  paymentDescription?: string;
  phoneNumber?: string;
  mobileNumber?: string;
  bankNameLabel?: string;
  bankCode?: string;
  bankAddress?: string;
}

interface LocalTransferProps {
  onNext?: () => void;
  setlocalBankInfo?: (data: FormInputs) => void;
  localBankInfo: FormInputs;
  setBankName: (data: string) => void;
  setInitialAccountName: (data: string) => void;
  initialAccountName: string;
}

const LocalBeneficiary = ({
  onNext,
  setlocalBankInfo,
  localBankInfo,
  setBankName,
  setInitialAccountName,
  initialAccountName,
}: LocalTransferProps) => {

  const toast = useToast();
  const dispatch = useAppDispatch();

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
    setValue
  } = useForm<FormInputs>({});

  const [beneficiaryState, setBeneficiaryState] = useState<LocalBeneficiaryState>({
    loading: false,
    activeBank: null,
    error: "no-error",
  });

  const { loading, activeBank, error } = beneficiaryState;
  const { userId, businessId } = useAppSelector((state) => state?.users);
  const getSavedSettlementDetails = useAppSelector((state) => state?.settlementPayout?.settlementDetails);
  const { currencyCode } = useParams();
  const accountNumberMaxLength = getAccountNumberMaxLength(currencyCode);
  const accountNumberPattern = getAccountNumberPattern(currencyCode);
  const accountNumberErrorMessage = getAccountNumberErrorMessage(currencyCode);
  const isGhsCurrency = capitalizeCurrencyCode(currencyCode) === "GHS";

  const formData = watch();

  const ghsPayout = formData?.payoutMethod === "ghsPayout"
  const momoGhsPayout = formData?.paymentMethod !== "BANK_ACCOUNT" && ghsPayout

  const { selectBanks: bankOptions, loading: banksLoading } = useBankList({
    country: capitalizeCurrencyCode(currencyCode),
    businessId,
  });

  const accountNumberValue = watch("accountNumber");

  const onCreateDefaultAccount = async () => {
    setBeneficiaryState({
      ...beneficiaryState,
      loading: true,
    });
    const addPayload = {
      bankCode: formData?.bankCode,
      accountNumber: formData?.accountNumber,
      accountName: formData?.accountName,
      country: settlementMethodCountryMap[formData?.payoutMethod],
      currency: settlementMethodCurrencyMap[formData?.payoutMethod],
      owner: userId,
      businessId: businessId,
    };

    try {
      await createUpdateBankAccount(addPayload);
      setBeneficiaryState({
        ...beneficiaryState,
        loading: false,
      });
      onNext();
    } catch (error) {
      toast?.notifyError(error?.response?.data?.error);
      setBeneficiaryState({
        ...beneficiaryState,
        loading: false,
      });
    }
  };

  const handleBankChange = (selectedBankLabel) => {
    const selectedBank = bankOptions.find(
      (bank) => bank.label === selectedBankLabel
    );
    setValue("bankCode", selectedBank?.originalCode ?? "");
    setBankName(selectedBank?.label);
    setlocalBankInfo(selectedBank);
    setBeneficiaryState((prev) => ({
      ...prev,
      activeBank: selectedBank?.label ?? null,
    }));
  };

  useEffect(() => {
    async function fetchData() {
      setBeneficiaryState((prev) => ({
        ...prev,
        loading: true,
      }));
      try {
        const data = await getBankAccountDetail({
          account_number: accountNumberValue,
          account_bank: formData.bankCode,
        });
        setlocalBankInfo(data);

        const { account_name } = data.data;
        setValue('accountName', account_name)

        if (data?.status === "success") {
          setBeneficiaryState((prev) => ({
            ...prev,
            error: "no-error",
            loading: false,
          }));
        } else if (!data?.status) {
          setBeneficiaryState((prev) => ({
            ...prev,
            error: "error",
            loading: false,
          }));
          setInitialAccountName("");
        }
      } catch (error) {
        setBeneficiaryState((prev) => ({
          ...prev,
          error: "error",
          loading: false,
        }));
        setInitialAccountName("");
      }
    }

    if (accountNumberValue?.length >= 10 && activeBank !== null) {
      fetchData();
    }
  }, [accountNumberValue, activeBank]);

  useEffect(() => {
    if (!isGhsCurrency && accountNumberValue?.length !== NGN_ACCOUNT_NUMBER_LENGTH) {
      setInitialAccountName("");
    }
  }, [accountNumberValue, setInitialAccountName, isGhsCurrency]);

  usePopulateFormFields<FormInputs>(getSavedSettlementDetails, setValue);

  const handleNext = (data) => {
    const payload = {
      ...getSavedSettlementDetails,
      ...data,
    }
    dispatch(setSettlementDetails(payload));
    onNext();
  }

  const isValidAccountNumber = accountNumberPattern.test(formData?.accountNumber ?? "");
  const hasInput = !!formData?.accountNumber?.length;

  return (
    <motion.div
      variants={nextVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <Loader isLoading={loading || banksLoading} />
      <Form>
        {ghsPayout && <InputBox>
          <Controller
            name="paymentMethod"
            control={control}
            rules={{ required: "Select a payout method." }}
            render={({ field }) => (
              <CustomSelect
                $height="40px"
                $width="100%"
                lightBorder
                placeholder="Select payment method"
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                }}
                options={ghsPayoutMethods}
                disabled
              />
            )}
          />
        </InputBox>}

        {!momoGhsPayout && <InputBox>
          <Controller
            name="bankName"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field }) => (
              <CustomSelect
                placeholder="Select Bank name"
                $width="100%"
                field={field}
                disableLabel
                options={bankOptions.map((bank) => ({
                  label: bank.label,
                  value: bank.label,
                }))}
                onChange={(selectedLabel) => {
                  field.onChange(selectedLabel);
                  handleBankChange(selectedLabel);
                }}
                showSearch
                filterOption={(inputValue, option) =>
                  option.label.toLowerCase().includes(inputValue.toLowerCase())
                }
                $height="40px"
                lightBorder
              />
            )}
          />
          {errors.bankName?.type === "required" && (
            <span>Please input the beneficiary&apos;s Bank name</span>
          )}
        </InputBox>}

        {ghsPayout && !momoGhsPayout && <InputBox>
          <Controller
            name="bankAddress"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field }) => (
              <Input
                type="text"
                label="Bank address"
                {...field}
                labelColor="#FFFFFF"
                height="40px"
                disableLabel
              />
            )}
          />
          {errors.bankAddress?.type === "required" && (
            <span>Please input the bank&apos;s address</span>
          )}
        </InputBox>}

        {!momoGhsPayout && <InputBox>
          <Controller
            name="accountNumber"
            control={control}
            rules={{
              required: true,
              pattern: accountNumberPattern,
            }}
            render={({ field }) => (
              <Input
                type="text"
                inputMode="numeric"
                label="Account Number"
                field={{
                  ...field,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(
                      sanitizeAccountNumberInput(
                        e.target.value,
                        accountNumberMaxLength,
                      ),
                    );
                  },
                }}
                labelColor="#FFFFFF"
                disableLabel
                height="40px"
              />
            )}
          />
          {hasInput && !isValidAccountNumber && (
            <ErrorMessage message={accountNumberErrorMessage} />
          )}
          {isValidAccountNumber && error === "error" && (
            <ErrorMessage message="Account number is not valid" />
          )}

          {errors.accountNumber?.type === "required" && (
            <span>Please input the beneficiary&apos;s account number</span>
          )}
        </InputBox>}

        {momoGhsPayout && (
          <>
            <InputBox>
              <Controller
                name="accountNumber"
                control={control}
                rules={{
                  required: true,
                  pattern: accountNumberPattern,
                }}
                render={({ field }) => (
                  <Input
                    type="text"
                    inputMode="numeric"
                    label="Mobile number"
                    field={{
                      ...field,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        field.onChange(
                          sanitizeAccountNumberInput(
                            e.target.value,
                            accountNumberMaxLength,
                          ),
                        );
                      },
                    }}
                    labelColor="#FFFFFF"
                    height="40px"
                  />
                )}
              />
              {hasInput && !isValidAccountNumber && (
                <ErrorMessage message={GHS_PHONE_NUMBER_ERROR_MESSAGE} />
              )}
              {errors.accountNumber?.type === "required" && (
                <span>Please input the beneficiary&apos;s mobile number</span>
              )}
            </InputBox>
          </>
        )}

        <InputBox>
          <Controller
            name="accountName"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                label={momoGhsPayout ? "Beneficiary name" : "Account Name"}
                labelColor="#FFFFFF"
                disabled={!ghsPayout}
                height="40px"
                disableLabel
              />
            )}
          />
        </InputBox>

        {ghsPayout &&
          <InputBox>
            <Controller
              name="paymentDescription"
              control={control}
              rules={{ required: "Payment description is required" }}
              render={({ field }) => (
                <CustomSelect
                  {...field}
                  options={GHS_PAYMENT_DESCRIPTION}
                  $width="100%"
                  placeholder="Select payment description"
                  showSearch
                  lightBorder
                />
              )}
            />
            {errors.paymentDescription?.message && (
              <span>{errors.paymentDescription.message}</span>
            )}
          </InputBox>
        }

        <Button
          label="Continue"
          height="48px"
          theme="primary"
          type="submit"
          disabled={
            error === "error" || loading || !isValidAccountNumber
          }
          onClick={handleSubmit((data) => {
            handleNext(data)
          })}
        />
      </Form>
    </motion.div>
  );
};

export default LocalBeneficiary;
